import { useCallback, useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/ContextoAuth'
import { sucursales as apiSucursales } from '../api/servicios'
import { mensajeDeError } from './usarCargar'
import type { Sucursal } from '../api/tipos'
import { AvisoConexion } from './AvisoConexion'
import { Dialogo } from './Dialogo'
import { usarTema } from '../estilos/tema'
import { Icono } from './Iconos'
import type { NombreIcono } from './Iconos'
import './layout.css'

interface Entrada {
    ruta: string
    etiqueta: string
    icono: NombreIcono
    /** Basta con tener **uno** de estos permisos para ver la entrada. */
    permisos?: string[]
    /** Se muestra en la barra inferior del telefono. */
    principal?: boolean
}

interface Grupo {
    /** Nulo en el primer grupo: lo de arriba no necesita presentacion. */
    titulo: string | null
    entradas: Entrada[]
}

const GRUPOS: Grupo[] = [
    {
        titulo: null,
        entradas: [
            { ruta: '/pos', etiqueta: 'Vender', icono: 'vender', permisos: ['venta.crear'], principal: true },
            { ruta: '/caja', etiqueta: 'Caja', icono: 'caja', permisos: ['caja.abrir'], principal: true },
            { ruta: '/productos', etiqueta: 'Productos', icono: 'productos', permisos: ['catalogo.ver'], principal: true },
            { ruta: '/productos/carga', etiqueta: 'Carga con lector', icono: 'productos', permisos: ['catalogo.editar'] },
            { ruta: '/inventario', etiqueta: 'Inventario', icono: 'inventario', permisos: ['inventario.ver'] },
            { ruta: '/traspasos', etiqueta: 'Traspasos', icono: 'inventario', permisos: ['inventario.ver'] },
            { ruta: '/devoluciones', etiqueta: 'Devoluciones', icono: 'devoluciones', permisos: ['devolucion.registrar'] },
            { ruta: '/clientes', etiqueta: 'Clientes', icono: 'clientes', permisos: ['credito.ver'] },
        ],
    },
    {
        titulo: 'Administración',
        entradas: [
            { ruta: '/reportes', etiqueta: 'Reportes', icono: 'reportes', permisos: ['reporte.ver'], principal: true },
            { ruta: '/promociones', etiqueta: 'Promociones', icono: 'promociones', permisos: ['promocion.administrar'] },
            { ruta: '/usuarios', etiqueta: 'Usuarios', icono: 'usuarios', permisos: ['usuario.administrar'] },
            { ruta: '/sucursales', etiqueta: 'Sucursales', icono: 'tienda', permisos: ['sucursal.administrar'] },
            // Quien solo puede sacar respaldos tambien necesita llegar aqui.
            {
                ruta: '/configuracion',
                etiqueta: 'Configuración',
                icono: 'configuracion',
                permisos: ['configuracion.editar', 'respaldo.ejecutar'],
            },
        ],
    },
]

const CLAVE_PLEGADO = 'minimarket.lateral.plegada'

/** Recuerda si la barra quedo plegada. Es una preferencia del equipo, no del usuario. */
function usarPlegado(): [boolean, () => void] {
    const [plegada, setPlegada] = useState(() => {
        try {
            return localStorage.getItem(CLAVE_PLEGADO) === '1'
        } catch {
            return false
        }
    })

    const alternar = useCallback(() => {
        setPlegada((actual) => {
            try {
                localStorage.setItem(CLAVE_PLEGADO, actual ? '0' : '1')
            } catch {
                // Sin persistencia dura lo que dure la pestania.
            }

            return !actual
        })
    }, [])

    return [plegada, alternar]
}

function iniciales(nombre: string): string {
    return nombre
        .split(/\s+/)
        .filter((palabra) => palabra.length > 0)
        .slice(0, 2)
        .map((palabra) => palabra[0]!.toUpperCase())
        .join('')
}

/** ADMINISTRADOR -> Administrador. Va debajo del nombre, no a los gritos. */
function rolLegible(rol: string | undefined): string {
    if (!rol) return 'Sin rol'

    return rol.charAt(0) + rol.slice(1).toLowerCase()
}

export function Layout() {
    const { usuario, salir, puede, cambiarSucursal } = useAuth()
    const { tema, alternar: alternarTema } = usarTema()
    const [plegada, alternarPlegada] = usarPlegado()
    const [cajonAbierto, setCajonAbierto] = useState(false)
    const [cambiandoLocal, setCambiandoLocal] = useState(false)
    const ubicacion = useLocation()

    // Al navegar se cierra el cajon: dejarlo abierto tapa la pantalla nueva.
    useEffect(() => {
        setCajonAbierto(false)
    }, [ubicacion.pathname])

    // Escape cierra el cajon, como cualquier otra capa superpuesta.
    useEffect(() => {
        if (!cajonAbierto) return

        function alPulsar(evento: KeyboardEvent) {
            if (evento.key === 'Escape') setCajonAbierto(false)
        }

        window.addEventListener('keydown', alPulsar)
        return () => window.removeEventListener('keydown', alPulsar)
    }, [cajonAbierto])

    // Un grupo sin entradas visibles no aporta su titulo: se descarta entero.
    const grupos = GRUPOS.map((grupo) => ({
        ...grupo,
        entradas: grupo.entradas.filter((e) => !e.permisos || e.permisos.some(puede)),
    })).filter((grupo) => grupo.entradas.length > 0)

    const principales = grupos.flatMap((g) => g.entradas).filter((e) => e.principal)
    const nombre = usuario?.nombreCompleto ?? ''
    const rol = rolLegible(usuario?.roles[0])

    // La sucursal va donde se ve sin buscarla. Con un solo local es un adorno;
    // con dos es la diferencia entre descontar el stock del local correcto y
    // no enterarse hasta el inventario.
    const sucursal = usuario?.sucursalNombre ?? null

    // Solo para quien recorre los locales. Un cajero no lo ve y no puede.
    const puedeCambiarLocal = puede('sucursal.cambiar')

    return (
        <div className={`armazon${plegada ? ' armazon-plegada' : ''}`}>
            {/* Barra superior: solo en teléfono y tablet angosta. */}
            <header className="cabecera">
                <button
                    type="button"
                    className="boton boton-fantasma"
                    onClick={() => setCajonAbierto(true)}
                    aria-label="Abrir menú"
                    aria-expanded={cajonAbierto}
                >
                    <Icono nombre="menu" tamanio={22} />
                </button>

                <span className="marca-compacta truncar">
                    <Icono nombre="tienda" tamanio={18} />
                    {sucursal ?? 'Minimarket'}
                </span>

                <div className="crece" />

                <button
                    type="button"
                    className="boton boton-fantasma"
                    onClick={alternarTema}
                    aria-label={tema === 'claro' ? 'Cambiar a tema oscuro' : 'Cambiar a tema claro'}
                >
                    <Icono nombre={tema === 'claro' ? 'luna' : 'sol'} tamanio={20} />
                </button>
            </header>

            {/* Barra lateral fija en escritorio. */}
            <div className="lateral">
                <PanelLateral
                    grupos={grupos}
                    plegada={plegada}
                    nombre={nombre}
                    rol={rol}
                    sucursal={sucursal}
                    alCambiarLocal={puedeCambiarLocal ? () => setCambiandoLocal(true) : undefined}
                    tema={tema}
                    alternarTema={alternarTema}
                    alSalir={() => void salir()}
                />

                <button
                    type="button"
                    className="pliegue"
                    onClick={alternarPlegada}
                    aria-label={plegada ? 'Expandir el menú' : 'Contraer el menú'}
                    title={plegada ? 'Expandir' : 'Contraer'}
                >
                    <Icono nombre={plegada ? 'desplegar' : 'plegar'} tamanio={16} />
                </button>
            </div>

            {/* Cajón deslizable en tablet y teléfono. Nunca plegado: si alguien
                se tomó el trabajo de abrirlo, quiere ver las etiquetas. */}
            {cajonAbierto && (
                <div className="capa" onClick={() => setCajonAbierto(false)} role="presentation">
                    <div className="cajon" onClick={(evento) => evento.stopPropagation()}>
                        <PanelLateral
                            grupos={grupos}
                            plegada={false}
                            nombre={nombre}
                            rol={rol}
                            sucursal={sucursal}
                            alCambiarLocal={puedeCambiarLocal ? () => setCambiandoLocal(true) : undefined}
                            tema={tema}
                            alternarTema={alternarTema}
                            alSalir={() => void salir()}
                        />
                    </div>
                </div>
            )}

            <main className="contenido">
                <AvisoConexion />
                <Outlet />
            </main>

            {/* Accesos directos al alcance del pulgar en el teléfono. */}
            <nav className="barra-inferior" aria-label="Accesos rápidos">
                {principales.map((entrada) => (
                    <NavLink
                        key={entrada.ruta}
                        to={entrada.ruta}
                        // Sin 'end', NavLink marca activo por prefijo y
                        // '/productos' quedaria encendido estando en
                        // '/productos/carga'. Ninguna entrada de este menu
                        // quiere ese comportamiento.
                        end
                        className="enlace-inferior"
                    >
                        <Icono nombre={entrada.icono} tamanio={22} />
                        <span className="diminuto">{entrada.etiqueta}</span>
                    </NavLink>
                ))}
            </nav>

            {cambiandoLocal && (
                <DialogoCambioSucursal
                    sucursalActualId={usuario?.sucursalId ?? null}
                    alElegir={async (id) => {
                        await cambiarSucursal(id)
                        setCambiandoLocal(false)
                    }}
                    alCerrar={() => setCambiandoLocal(false)}
                />
            )}
        </div>
    )
}

/* -------------------------------------------------------------------------- */

/**
 * Elegir en que local operar.
 *
 * Cambiar emite tokens nuevos y descarta los anteriores, asi que no es un
 * filtro de pantalla: a partir de ahi las ventas, la caja y el stock son los
 * del local elegido. Por eso se pregunta en un dialogo y no en un desplegable
 * que se toca sin querer.
 */
function DialogoCambioSucursal({
    sucursalActualId,
    alElegir,
    alCerrar,
}: {
    sucursalActualId: number | null
    alElegir: (sucursalId: number) => Promise<void>
    alCerrar: () => void
}) {
    const [lista, setLista] = useState<Sucursal[] | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)

    useEffect(() => {
        let vigente = true

        apiSucursales
            .listar(true)
            .then((s) => {
                if (vigente) setLista(s)
            })
            .catch((fallo) => {
                if (vigente) setError(mensajeDeError(fallo))
            })

        return () => {
            vigente = false
        }
    }, [])

    async function elegir(sucursalId: number) {
        setError(null)
        setEnviando(true)

        try {
            await alElegir(sucursalId)
        } catch (fallo) {
            setError(mensajeDeError(fallo))
            setEnviando(false)
        }
    }

    const otras = (lista ?? []).filter((s) => s.sucursalId !== sucursalActualId)

    return (
        <Dialogo
            titulo="Cambiar de local"
            alCerrar={alCerrar}
            acciones={
                <button type="button" className="boton" onClick={alCerrar}>
                    Cancelar
                </button>
            }
        >
            <div className="columna g3">
                <p className="tenue chico">
                    Al cambiar, las ventas, la caja y el stock pasan a ser los del local que elija.
                    Se cierra la sesión actual y se abre una nueva.
                </p>

                {lista === null && !error && <p className="tenue">Cargando…</p>}

                {lista !== null && otras.length === 0 && (
                    <p className="tenue">No hay otro local activo al que cambiar.</p>
                )}

                <ul className="lista-simple">
                    {otras.map((s) => (
                        <li key={s.sucursalId}>
                            <button
                                type="button"
                                className="boton"
                                style={{ width: '100%' }}
                                disabled={enviando}
                                onClick={() => void elegir(s.sucursalId)}
                            >
                                {s.nombre}
                            </button>
                        </li>
                    ))}
                </ul>

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}
            </div>
        </Dialogo>
    )
}

/* -------------------------------------------------------------------------- */

interface PropsPanel {
    grupos: Grupo[]
    plegada: boolean
    nombre: string
    rol: string
    sucursal: string | null
    /** Presente solo si el usuario puede cambiar de local. */
    alCambiarLocal?: () => void
    tema: 'claro' | 'oscuro'
    alternarTema: () => void
    alSalir: () => void
}

function PanelLateral({
    grupos,
    plegada,
    nombre,
    rol,
    sucursal,
    alCambiarLocal,
    tema,
    alternarTema,
    alSalir,
}: PropsPanel) {
    return (
        <div className="panel-lateral">
            <div className="marca">
                <span className="marca-icono">
                    <Icono nombre="tienda" tamanio={20} />
                </span>

                {!plegada && (
                    <span className="columna marca-texto">
                        <strong>Minimarket</strong>

                        {alCambiarLocal ? (
                            <button
                                type="button"
                                className="cambiar-local diminuto truncar"
                                onClick={alCambiarLocal}
                                title="Cambiar de local"
                            >
                                {sucursal ?? 'Punto de venta'}
                            </button>
                        ) : (
                            <span className="diminuto tenue truncar" title={sucursal ?? undefined}>
                                {sucursal ?? 'Punto de venta'}
                            </span>
                        )}
                    </span>
                )}
            </div>

            <nav className="navegacion" aria-label="Navegación principal">
                {grupos.map((grupo, indice) => (
                    <div key={grupo.titulo ?? `grupo-${indice}`} className="grupo-nav">
                        {grupo.titulo &&
                            (plegada ? (
                                <span className="separador-nav" aria-hidden="true" />
                            ) : (
                                <span className="titulo-grupo">{grupo.titulo}</span>
                            ))}

                        {grupo.entradas.map((entrada) => (
                            <NavLink
                                key={entrada.ruta}
                                to={entrada.ruta}
                                end
                                className="enlace-nav"
                                // Con la barra plegada, el nombre solo vive acá.
                                title={plegada ? entrada.etiqueta : undefined}
                            >
                                <Icono nombre={entrada.icono} />
                                {!plegada && <span className="truncar">{entrada.etiqueta}</span>}
                            </NavLink>
                        ))}
                    </div>
                ))}
            </nav>

            <div className="pie-lateral">
                <button
                    type="button"
                    className="enlace-nav boton-tema"
                    onClick={alternarTema}
                    aria-label={tema === 'claro' ? 'Cambiar a tema oscuro' : 'Cambiar a tema claro'}
                    title={plegada ? (tema === 'claro' ? 'Modo oscuro' : 'Modo claro') : undefined}
                >
                    <Icono nombre={tema === 'claro' ? 'luna' : 'sol'} />
                    {!plegada && <span>{tema === 'claro' ? 'Modo oscuro' : 'Modo claro'}</span>}
                </button>

                <div className="tarjeta-usuario">
                    <span className="avatar" aria-hidden="true">
                        {iniciales(nombre || '?')}
                    </span>

                    {!plegada && (
                        <span className="columna crece datos-usuario">
                            <span className="chico truncar">{nombre}</span>
                            <span className="diminuto tenue truncar">{rol}</span>
                        </span>
                    )}

                    <button
                        type="button"
                        className="boton boton-fantasma salir"
                        onClick={alSalir}
                        aria-label="Cerrar sesión"
                        title="Cerrar sesión"
                    >
                        <Icono nombre="salir" tamanio={18} />
                    </button>
                </div>
            </div>
        </div>
    )
}
